import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft, Tag, Share2, Facebook, Twitter } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import ktcLogo from '../assets/ktc.png';
import { resolveBackendBaseUrl } from '../utils/runtimeConfig';
import { sanitizeNewsHtml } from '../utils/newsContent';

const API = `${resolveBackendBaseUrl()}/api`;

const NewsDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNewsDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/news/${id}`);
      setNews(response.data);

      // Fetch related news
      const allNews = await axios.get(`${API}/news`, { params: { category: response.data.category, limit: 4 } });
      setRelatedNews(allNews.data.filter(item => item.id !== id).slice(0, 3));
    } catch (error) {
      console.error('Error fetching news:', error);
      navigate('/berita');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchNewsDetail();
  }, [fetchNewsDetail]);
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const shareUrl = window.location.href;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-brand-blue border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-400 mt-4">Memuat berita...</p>
        </div>
      </div>
    );
  }

  if (!news) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={ktcLogo} 
                alt="KTC Logo" 
                className="h-10 w-auto"
              />
              <span className="font-outfit font-bold text-lg text-white hidden sm:block">
                Kedai Teknologi Celebes
              </span>
            </Link>
            <Link to="/berita">
              <Button variant="outline" className="btn-secondary flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Semua Berita
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Image */}
      <div className="relative h-[40vh] md:h-[50vh]">
        <img 
          src={news.image_url || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200'}
          alt={news.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 md:px-12 -mt-32 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#0A0A0A] rounded-2xl p-8 md:p-12 border border-white/10"
        >
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-3 py-1 bg-brand-red/20 text-brand-red text-sm font-medium rounded-full flex items-center gap-1">
              <Tag className="w-3 h-3" />
              {news.category}
            </span>
            <span className="flex items-center gap-2 text-gray-500 text-sm">
              <Calendar className="w-4 h-4" />
              {formatDate(news.created_at)}
            </span>
            <span className="flex items-center gap-2 text-gray-500 text-sm">
              <User className="w-4 h-4" />
              {news.author}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight">
            {news.title}
          </h1>

          {/* Content */}
          <div
            className="prose prose-lg prose-invert max-w-none news-rich-content"
            dangerouslySetInnerHTML={{ __html: sanitizeNewsHtml(news.content || '') }}
          />

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <span className="text-gray-400 flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                Bagikan artikel ini:
              </span>
              <div className="flex gap-3">
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-[#1877F2]/20 flex items-center justify-center text-[#1877F2] hover:bg-[#1877F2]/30 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${news.title}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-[#1DA1F2]/20 flex items-center justify-center text-[#1DA1F2] hover:bg-[#1DA1F2]/30 transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Related News */}
        {relatedNews.length > 0 && (
          <section className="mt-16 mb-16">
            <h2 className="font-outfit text-2xl font-bold text-white mb-8">
              Berita <span className="gradient-text-blue">Terkait</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedNews.map((item) => (
                <Link 
                  key={item.id}
                  to={`/berita/${item.id}`}
                  className="service-card bg-[#121212] rounded-xl overflow-hidden group"
                >
                  <div className="h-40 overflow-hidden">
                    <img 
                      src={item.image_url || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400'}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-gray-500 mb-2">{formatDate(item.created_at)}</p>
                    <h3 className="font-outfit font-semibold text-white line-clamp-2 group-hover:text-brand-blue transition-colors">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10 mt-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Kedai Teknologi Celebes. Seluruh hak cipta dilindungi.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default NewsDetailPage;





